import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiGet } from "@/lib/api";
import { useSession } from "@/lib/auth";
import type { WorkspaceRecord } from "@/lib/workspace";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import {
  createIncomingWebhook,
  discardIncomingWebhookQueries,
  incomingWebhookDeliveriesKey,
  incomingWebhookListKey,
  incomingWebhookRoomsKey,
  listIncomingWebhookDeliveries,
  listIncomingWebhooks,
  revokeIncomingWebhook,
  rotateIncomingWebhook,
} from "./api";
import { formatWebhookTimestamp, shortWebhookId, webhookStatusLabel } from "./format";
import { selectWebhookDestinationRooms } from "./rooms";
import type { IncomingWebhookRecord, IncomingWebhookSecretResponse } from "./types";
import { isWorkspaceAdminRole, validateWebhookDisplayName } from "./validation";

interface IssuedSecret {
  url: string | null;
  webhook: IncomingWebhookRecord;
  resumed: boolean;
  workspaceName: string;
  roomName: string;
  scope: string;
}

interface BoundOperation {
  id: string;
  room_id: string;
  display_name: string;
}

function currentScope(userId: string, tenantId: string) {
  return `${userId}:${tenantId}`;
}

export function IncomingWebhooksPanel({ workspace }: { workspace: WorkspaceRecord }): JSX.Element | null {
  const session = useSession();
  const queryClient = useQueryClient();
  const userId = session?.user.id ?? "";
  const tenantId = session?.tenantId || workspace.tenant_id || workspace.id;
  const scope = currentScope(userId, tenantId);
  const allowed = isWorkspaceAdminRole(workspace.role);
  const [displayName, setDisplayName] = useState("");
  const [roomId, setRoomId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedSecret | null>(null);
  const [historyHook, setHistoryHook] = useState<IncomingWebhookRecord | null>(null);
  const [copied, setCopied] = useState(false);
  const operation = useRef<BoundOperation | null>(null);
  const prevScope = useRef(scope);

  useEffect(() => {
    if (prevScope.current === scope) return;
    const [prevUser, prevTenant] = prevScope.current.split(":");
    if (prevUser) discardIncomingWebhookQueries(queryClient, { userId: prevUser, tenantId: prevTenant ?? "" });
    prevScope.current = scope;
    setIssued(null);
    setHistoryHook(null);
    setDisplayName("");
    setRoomId("");
    setError(null);
    setCopied(false);
    setBusy(false);
    operation.current = null;
  }, [queryClient, scope]);

  const list = useQuery({
    queryKey: incomingWebhookListKey(userId, tenantId),
    enabled: allowed && !!userId && !!workspace.id,
    retry: false,
    queryFn: () => listIncomingWebhooks(workspace.id),
  });
  const rooms = useQuery({
    queryKey: incomingWebhookRoomsKey(userId, tenantId),
    enabled: allowed && !!userId,
    retry: false,
    queryFn: async () => {
      const result = await apiGet<{ rooms: unknown[] }>("/v1/rooms");
      return selectWebhookDestinationRooms(result.rooms);
    },
  });

  const destinations = rooms.data ?? [];
  const selectedRoom = destinations.some((room) => room.id === roomId) ? roomId : destinations[0]?.id ?? "";
  const history = useQuery({
    queryKey: incomingWebhookDeliveriesKey(userId, tenantId, historyHook?.id ?? ""),
    enabled: allowed && !!historyHook,
    retry: false,
    queryFn: () => listIncomingWebhookDeliveries(workspace.id, historyHook!.id),
  });

  if (!allowed) return null;

  const liveIssued = issued?.scope === scope ? issued : null;
  const issuedUrl = liveIssued?.url ?? null;

  const stillCurrent = (requestScope: string) => prevScope.current === requestScope;

  const rememberIssued = (requestScope: string, result: IncomingWebhookSecretResponse) => {
    if (!stillCurrent(requestScope)) return;
    setIssued({
      url: result.url,
      webhook: result.webhook,
      resumed: result.resumed === true,
      workspaceName: workspace.name,
      roomName: result.webhook.room_name,
      scope: requestScope,
    });
  };

  const clearIssued = () => {
    setIssued(null);
    setCopied(false);
  };

  const run = async (work: (requestScope: string) => Promise<void>) => {
    const requestScope = scope;
    setBusy(true);
    setError(null);
    try {
      await work(requestScope);
    } catch (e) {
      if (!stillCurrent(requestScope)) return;
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : "操作に失敗しました。");
    } finally {
      if (stillCurrent(requestScope)) setBusy(false);
    }
  };

  const onCreate = () => {
    const name = displayName.trim();
    const nameError = validateWebhookDisplayName(displayName);
    if (nameError) {
      setError(nameError);
      return;
    }
    if (!selectedRoom) {
      setError("投稿できる公開または非公開ルームがありません。");
      return;
    }
    const bound = operation.current;
    if (!bound || bound.room_id !== selectedRoom || bound.display_name !== name) {
      operation.current = { id: crypto.randomUUID(), room_id: selectedRoom, display_name: name };
    }
    void run(async (requestScope) => {
      const result = await createIncomingWebhook(workspace.id, {
        room_id: selectedRoom,
        display_name: name,
        operation_id: operation.current?.id,
      });
      if (!stillCurrent(requestScope)) return;
      operation.current = null;
      rememberIssued(requestScope, result);
      setDisplayName("");
      await list.refetch();
    });
  };

  const onRotate = (hook: IncomingWebhookRecord) => {
    if (!window.confirm(`${hook.display_name} の URL を再発行しますか？現在の URL はすぐに使えなくなります。`)) return;
    void run(async (requestScope) => {
      const result = await rotateIncomingWebhook(workspace.id, hook.id);
      rememberIssued(requestScope, result);
      if (stillCurrent(requestScope)) await list.refetch();
    });
  };

  const onRevoke = (hook: IncomingWebhookRecord) => {
    if (!window.confirm(`${hook.display_name} を失効しますか？この URL では投稿できなくなります。`)) return;
    void run(async (requestScope) => {
      await revokeIncomingWebhook(workspace.id, hook.id);
      if (!stillCurrent(requestScope)) return;
      if (historyHook?.id === hook.id) setHistoryHook(null);
      await list.refetch();
    });
  };

  const copyUrl = async () => {
    if (!issuedUrl) return;
    const write = navigator.clipboard?.writeText;
    if (typeof write !== "function") {
      setCopied(false);
      setError("コピーできませんでした。URL を選択してコピーしてください。");
      return;
    }
    try {
      await write.call(navigator.clipboard, issuedUrl);
      setCopied(true);
    } catch {
      setCopied(false);
      setError("コピーできませんでした。URL を選択してコピーしてください。");
    }
  };

  return (
    <section className="space-y-3 rounded-lg border border-border-default p-4" aria-label="Incoming Webhooks 接続">
      <div>
        <h3 className="font-semibold">Incoming Webhooks 接続</h3>
        <p className="text-sm text-fg-secondary">
          企業「{workspace.name}」のチャンネルへ外部から平文を投稿する URL を発行します。URL は発行・再発行のときだけ表示し、保存しません。
        </p>
      </div>
      {(error || list.error || rooms.error) && (
        <p role="alert" className="text-danger text-sm">
          {error || (list.error instanceof Error ? list.error.message : rooms.error instanceof Error ? rooms.error.message : "読み込みに失敗しました。")}
        </p>
      )}
      {list.isPending ? <p role="status">Incoming Webhooks を読み込んでいます…</p> : null}
      <form
        className="space-y-3 rounded border border-border-subtle p-3"
        onSubmit={(e) => {
          e.preventDefault();
          onCreate();
        }}
      >
        <label className="block text-sm space-y-1">
          表示名
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={80}
            disabled={busy}
            aria-label="Webhook 表示名"
            placeholder="例: デプロイ通知"
          />
        </label>
        <label className="block text-sm space-y-1">
          投稿先ルーム
          <select
            className="w-full h-10 rounded-md border border-border-default bg-canvas px-3 text-sm"
            value={selectedRoom}
            onChange={(e) => setRoomId(e.target.value)}
            disabled={busy || !destinations.length}
            aria-label="投稿先ルーム"
          >
            {!destinations.length ? <option value="">投稿できるルームがありません</option> : null}
            {destinations.map((room) => (
              <option key={room.id} value={room.id}>
                {room.name}（{room.kind === "public" ? "公開" : "非公開"}）
              </option>
            ))}
          </select>
        </label>
        <Button type="submit" size="sm" disabled={busy || !destinations.length} loading={busy}>
          URL を発行
        </Button>
      </form>
      {!list.isPending && !list.error && (list.data?.webhooks?.length ?? 0) === 0 ? (
        <p role="status" className="text-sm text-fg-secondary">Incoming Webhook はまだありません。</p>
      ) : null}
      {(list.data?.webhooks?.length ?? 0) > 0 ? (
        <ul className="space-y-2">
          {(list.data?.webhooks ?? []).map((hook) => (
            <li key={hook.id} className="rounded border border-border-subtle p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <code className="text-xs">{shortWebhookId(hook.id)}</code>
                <Badge tone={hook.status === "active" ? "success" : hook.status === "revoked" ? "danger" : "warning"}>
                  {webhookStatusLabel(hook.status)}
                </Badge>
                <span className="font-medium text-sm">{hook.display_name}</span>
              </div>
              <p className="text-xs text-fg-muted">
                {hook.room_name || "ルーム"} · 最終利用 {formatWebhookTimestamp(hook.last_used_at)}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" disabled={busy || hook.status === "revoked"} onClick={() => onRotate(hook)}>
                  再発行
                </Button>
                <Button size="sm" variant="danger" disabled={busy || hook.status === "revoked"} onClick={() => onRevoke(hook)}>
                  失効
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => setHistoryHook(hook)}>
                  受信履歴
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      {liveIssued && !issuedUrl ? (
        <p role="status" className="text-sm text-fg-secondary">
          秘密 URL は再表示できません。企業「{liveIssued.workspaceName}」の「{liveIssued.roomName || liveIssued.webhook.room_name || "ルーム"}」向けです。一覧から再発行してください。
        </p>
      ) : null}
      <Modal open={!!issuedUrl} onClose={clearIssued} title="Webhook URL" size="lg">
        <div className="p-5 space-y-3">
          <p className="text-sm text-fg-secondary">この URL は今だけ表示されます。閉じると再表示できません。</p>
          <p className="text-sm">企業「{liveIssued?.workspaceName ?? workspace.name}」</p>
          <p className="text-sm">投稿先「{liveIssued?.roomName || liveIssued?.webhook.room_name || "ルーム"}」</p>
          <label className="block text-sm space-y-1">
            発行した URL
            <Input readOnly value={issuedUrl ?? ""} aria-label="発行した Webhook URL" />
          </label>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void copyUrl()}>{copied ? "コピーしました" : "コピー"}</Button>
            <Button size="sm" variant="secondary" onClick={clearIssued}>閉じる</Button>
          </div>
        </div>
      </Modal>
      <Modal open={!!historyHook} onClose={() => setHistoryHook(null)} title="受信履歴" size="lg">
        <div className="p-5 space-y-3">
          <p className="text-sm text-fg-secondary">
            {historyHook ? `${historyHook.display_name}（${shortWebhookId(historyHook.id)}）` : ""} · 企業「{workspace.name}」
          </p>
          {history.isPending ? <p role="status">受信履歴を読み込んでいます…</p> : null}
          {history.error ? <p role="alert" className="text-danger text-sm">{history.error.message}</p> : null}
          {!history.isPending && !history.error && (history.data?.deliveries?.length ?? 0) === 0 ? (
            <p role="status" className="text-sm text-fg-secondary">受信履歴はありません。</p>
          ) : null}
          <ul className="space-y-2">
            {(history.data?.deliveries ?? []).map((row) => (
              <li key={row.id} className="text-sm rounded border border-border-subtle p-2">
                <span>{formatWebhookTimestamp(row.created_at)}</span>
                <span> · HTTP {row.http_status}</span>
                {row.error_code ? <span> · {row.error_code}</span> : null}
                {row.message_id ? <span className="text-fg-muted"> · {shortWebhookId(row.message_id)}</span> : null}
              </li>
            ))}
          </ul>
          <Button size="sm" variant="secondary" onClick={() => setHistoryHook(null)}>閉じる</Button>
        </div>
      </Modal>
    </section>
  );
}
